import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import { resolvePermissionCheck } from '@/utils/groupPermissions';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { limit: 20, ordering: 'owner_name,name' };

const createState = (options = {}) => {
    return {
        url: '/api/catalogs/',
        catalogs: [],
        catalogs_count: 0,
        catalogs_loading: false,
        catalogs_loaded: false,
        catalogs_loading_errors: null,
        catalogs_filters: { offset: 0, ...DEFAULT_FILTERS },
        cancelTokenSource: null,
        
        _storageOptions: options || {},
    };
};


const mutations = {

    updateCatalogs(state, catalogs) {
        state.catalogs = catalogs;
    },
    updateCatalogsCount(state, count) {
        state.catalogs_count = count;
    },
    updateCatalogsLoading(state, loading) {
        state.catalogs_loading = loading;
    },
    updateCatalogsLoaded(state, loaded) {
        state.catalogs_loaded = loaded;
    },
    updateCatalogsLoadingErrors(state, errors) {
        state.catalogs_loading_errors = errors;
    },
    updateCatalogsFilters(state, filters) {
        state.catalogs_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.catalogs_filters, fieldsToSave);
        }
    },
    updateUrl(state, url) {
        state.url = url;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
};

const actions = {

    async setCatalogAsDefault({ commit, dispatch, state, rootGetters }) {
        if (!resolvePermissionCheck(rootGetters['session/current_user_permissions'], "catalog.change_sellcatalog")) {
            return;
        }

        const url = `${state.url}${state.catalog.id}/default/`;
        try {
            const response = await axios.post(url);
            dispatch('fetchCatalog');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchCatalogs({ commit, state, rootGetters }, params = {}) {
        const silent = Boolean(params && params.silent);
        if (!resolvePermissionCheck(rootGetters['session/current_user_permissions'], "catalog.view_sellcatalog")) {
            commit('updateCatalogsLoading', false);
            commit('updateCatalogsLoaded', true);
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);
        if (!silent) {
            commit('updateCatalogsLoading', true);
        }
        // Keep catalogs_loaded true on refetch so the UI can keep showing rows.
        commit('updateCatalogsLoadingErrors', null);

        try {
            const response = await axios.get(state.url, {
                params: state.catalogs_filters,
                cancelToken: source.token,
            });
            commit('updateCatalogs', response.data.results);
            commit('updateCatalogsCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateCatalogsLoadingErrors', error.details);
            throw error;
        } finally {
            if (!silent) {
                commit('updateCatalogsLoading', false);
            }
            commit('updateCatalogsLoaded', true);
        }
    },

    async deleteCatalog({ commit, state, rootGetters }, params) {
        if (!resolvePermissionCheck(rootGetters['session/current_user_permissions'], "catalog.delete_sellcatalog")) {
            return;
        }

        const url = `${state.url}${params.instance.id}/`;
        try {
            const response = await axios.post(`${url}archive/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async removeCatalog({ state }, params) {
        try {
            const url = `/api/catalogs/${params.instance.id}/remove/`;
            const response = await axios.post(url);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async init({ commit, state }, params) {
        // Ready for a deferred fetch: do not leave loading=true without a request.
        commit('updateCatalogsLoading', false);
        commit('updateCatalogsLoaded', false);
        commit('updateCatalogs', []);
        commit('updateCatalogsCount', 0);
        commit('updateUrl', params.url || '/api/catalogs/');

        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            commit('updateStorageOptions', params.storageOptions);
        }

        // Load filters from localStorage if storage is configured
        commit(
            'updateCatalogsFilters',
            loadFiltersFromStorage(state._storageOptions.storageKey, params.filters || DEFAULT_FILTERS)
        );
    },

    resetCatalogsList({ commit }) {
        commit('updateCatalogsLoading', true);
        commit('updateCatalogsLoaded', false);
        commit('updateCatalogs', []);
        commit('updateCatalogsCount', 0);
    },

};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});